/*
 * Copyright (C) 2024 Webformat S.r.l.
 * https://www.webformat.com
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

/* eslint-disable no-prototype-builtins */
import axios from 'axios';
import store from '@/store';
import {get as dataGetter} from 'lodash';
import giftcard from '@/store/modules/giftcard';
import {
    AssignAddressRequest,
    CartItem,
    CartResponse,
    Customer,
    EstimateShippingRequest,
    Gift,
    LoginRequest,
    LoyaltyBenefit,
    Message,
    PaymentInformationRequest,
    PaymentMethod,
    QuoteAddress,
    RegistrationRequest,
    SaveNewsletterResponse,
    ShippingInformationRequest as ShipInfoReq,
    ShippingInformationResponse as ShipInfoResp,
    ShippingMethod,
    Totals,
    UpdateCartRequest,
    UpdateGiftRequest,
    UpdatePaymentRequest,
    UserRegistrationRequest,
} from './models';

const baseURL =
    process.env.NODE_ENV === 'production'
        ? `${store.state.options.base_url}rest/${store.state.options.store_code}/V1`
        : `/rest/${store.state.options.store_code}/V1`;

function cartUrl(endpoint: string): string {
    const base = '';
    if (process.env.NODE_ENV !== 'production') {
        const appCartId = process.env.VUE_APP_CARTID;
        if (process.env.VUE_APP_FLOW === 'guest') {
            return base + endpoint.replace(':cartApi', 'guest-carts').replace(':cartId', appCartId);
        }

        if (
            (process.env.VUE_APP_FLOW === 'loggedsess' && localStorage.getItem('usertoken')) ||
            process.env.VUE_APP_FLOW === 'logged'
        ) {
            return base + endpoint.replace(':cartApi', 'carts').replace(':cartId', 'mine');
        }

        return base + endpoint.replace(':cartApi', 'guest-carts').replace(':cartId', appCartId);
    }
    if (store.getters['user/isLoggedin']) {
        return base + endpoint.replace(':cartApi', 'carts').replace(':cartId', 'mine');
    }

    return base + endpoint.replace(':cartApi', 'guest-carts').replace(':cartId', store.getters['cart/token']);
}

function buildControllerRestClient() {
    return axios.create({
        baseURL: store.state.options.base_url,
        withCredentials: true,
    });
}

export const restClient = axios.create({
    baseURL,
    withCredentials: true,
    headers: {'X-VUECHECKOUT': '1'},
});

const pendingRequests: any = {};

// eslint-disable-next-line consistent-return
function sleepUntillPending(pendingKey: string, resolveCb: any, depsConfig: any): any {
    let allDepsFinished = true;

    if (depsConfig.hasOwnProperty(pendingKey)) {
        const deps = depsConfig[pendingKey] as string[];

        deps.forEach((dep) => {
            allDepsFinished = allDepsFinished && (!pendingRequests.hasOwnProperty(dep) || pendingRequests[dep] === 0);
        });
    }

    if (resolveCb) {
        if (allDepsFinished) {
            resolveCb();
        } else {
            setTimeout(() => {
                sleepUntillPending(pendingKey, resolveCb, depsConfig);
            }, 500);
        }
    } else {
        return new Promise<void>((resolve) =>
            // eslint-disable-next-line no-promise-executor-return
            setTimeout(() => {
                if (!allDepsFinished) {
                    sleepUntillPending(pendingKey, resolve, depsConfig);
                } else {
                    resolve();
                }
            }, 1000)
        );
    }
}

function getCleanedStore() {
    const s: any = store.state;
    const storeRes = {
        checkoutEmail: dataGetter(s, 'checkout.email'),
        billingId: dataGetter(s, 'checkout.billingAddress.id'),
        shippingId: dataGetter(s, 'checkout.shippingAddress.id'),
        selectedPayment: dataGetter(s, 'checkout.selectedPayment'),
        selectedShip: dataGetter(s, 'checkout.selectedShipment'),
        reqInvoice: dataGetter(s, 'checkout.requestInvoice'),
        billingSame: dataGetter(s, 'checkout.billingSameAsShipping'),
        coupon: dataGetter(s, 'checkout.coupon.coupon'),
        userId: dataGetter(s, 'user.customer.id'),
        cartToken: dataGetter(s, 'cart.cartToken'),
        cartId: dataGetter(s, 'cart.cart.id'),
        payment: {
            registry: dataGetter(s, 'payment.registry'),
            cc: {
                cardEmpty: dataGetter(s, 'payment.methodData[1].hostedFields._state.fields.number.isEmpty'),
                cardValid: dataGetter(s, 'payment.methodData[1].hostedFields._state.fields.number.isValid'),
                cvvEmpty: dataGetter(s, 'payment.methodData[1].hostedFields._state.fields.cvv.isEmpty'),
                cvvValid: dataGetter(s, 'payment.methodData[1].hostedFields._state.fields.cvv.isValid'),
                expEmpty: dataGetter(s, 'payment.methodData[1].hostedFields._state.fields.expirationDate.isEmpty'),
                expValid: dataGetter(s, 'payment.methodData[1].hostedFields._state.fields.expirationDate.isValid'),
            },
            sale: dataGetter(s, 'payment.salePayload'),
        },
    };

    return storeRes;
}

export const registeredInterceptors: any = {
    'intercept-request-mergecart': restClient.interceptors.request.use((config) => {
        if (config.url && config.url.indexOf('integration/customer/token') !== -1) {
            Object.assign(config.headers, {'X-MERGECARTSTOKEN': config.data.cartToken});
        }

        return config;
    }),
    'debug-request-debugmodeheader': restClient.interceptors.request.use((config) => {
        if (process.env.NODE_ENV !== 'production') {
            Object.assign(config.headers, {'X-DEBUGMODE': 1});
        }

        return config;
    }),
    'debug-request-addpending': restClient.interceptors.request.use((config) => {
        if (process.env.NODE_ENV !== 'production') {
            const url = config.url as string;

            if (!pendingRequests.hasOwnProperty(url)) {
                pendingRequests[url] = 0;
            }

            pendingRequests[url] += 1;
        }

        return config;
    }),
    'debug-request-waitresolveuser': restClient.interceptors.request.use((config) => {
        const url = config.url as string;
        const affected = ['carts/mine/totals'];
        const deps = {
            'carts/mine/totals': ['integration/customer/token'],
        };

        if (process.env.NODE_ENV !== 'production' && affected.includes(url)) {
            return new Promise((resolve) => {
                setTimeout(() => {
                    sleepUntillPending(url, null, deps).then(() => {
                        Object.assign(config.headers, restClient.defaults.headers);
                        resolve(config);
                    });
                }, 500);
            });
        }

        return config;
    }),
    'debug-response-removepending': restClient.interceptors.response.use(
        (response) => {
            if (process.env.NODE_ENV !== 'production') {
                const url = response.config.url as string;

                if (pendingRequests.hasOwnProperty(url)) {
                    pendingRequests[url] -= 1;
                }
            }

            return response;
        },
        (error) => {
            if (process.env.NODE_ENV !== 'production') {
                const url = error.config.url as string;

                if (pendingRequests.hasOwnProperty(url)) {
                    pendingRequests[url] -= 1;
                }
            }

            return Promise.reject(error);
        }
    ),
};

if (process.env.NODE_ENV === 'production') {
    for (const p in registeredInterceptors) {
        if (p.indexOf('debug-request') !== -1) {
            restClient.interceptors.request.eject(registeredInterceptors[p]);
        } else if (p.indexOf('debug-response') !== -1) {
            restClient.interceptors.response.eject(registeredInterceptors[p]);
        }
    }
}

export async function mockData(email?: string): Promise<any> {
    const resp = await restClient.get(email ? `mockdata/${email}` : 'mockdata');

    return JSON.parse(resp.data);
}

export async function saveLog(log: string): Promise<string> {
    const uniqId = `${Date.now()}_${Math.random().toString().slice(2)}`;
    const payload = {
        log,
        store: getCleanedStore(),
        uniqId,
    };
    const resp = await restClient.post('saveLog', payload);

    return resp.data;
}

export function setJWT(token: string): void {
    if (process.env.VUE_APP_FLOW === 'loggedsess') {
        localStorage.setItem('usertoken', token);
    }

    restClient.defaults.headers.Authorization = `Bearer ${token}`;
}

export function clearJWT(): void {
    delete restClient.defaults.headers.Authorization;
}

export async function login(request: LoginRequest): Promise<string> {
    if (request.recaptchaToken !== undefined && request.recaptchaToken !== '') {
        restClient.defaults.headers['X-ReCaptcha'] = request.recaptchaToken;
    }
    delete request.recaptchaToken;
    const resp = await restClient.post('integration/customer/token', request);

    return resp.data;
}

export async function forgotPassword(email: string): Promise<boolean> {
    const template = 'email_reminder';
    const resp = await restClient.put('customers/password', {email, template});

    return resp.data;
}

export async function userRegistration(request: RegistrationRequest): Promise<boolean> {
    if (request.recaptchaToken !== undefined && request.recaptchaToken !== '') {
        restClient.defaults.headers['X-ReCaptcha'] = request.recaptchaToken;
    }
    delete request.recaptchaToken;
    const resp = await restClient.post('customers', {customer: request.user, password: request.password});

    return resp.data;
}

export async function userSaveData(request: UserRegistrationRequest): Promise<Customer> {
    const resp = await restClient.put('customers/me', {customer: request});

    return resp.data;
}

export async function isEmailAvailable(email: string): Promise<boolean> {
    const resp = await restClient.post('customers/isEmailAvailable', {customerEmail: email});

    return resp.data;
}

export async function saveNewsletterAgreement(email: string): Promise<SaveNewsletterResponse> {
    const ep = 'newsletter/register';
    const resp = await restClient.post(ep, {email});

    return resp.data;
}

export async function getUserInfo(): Promise<Customer> {
    const resp = await restClient.get('customers/me');

    return resp.data as Customer;
}

export async function getCart(): Promise<CartResponse> {
    const resp = await restClient.get(cartUrl(':cartApi/:cartId'));

    return resp.data as CartResponse;
}

export async function assignEmailToQuote(email: string): Promise<boolean> {
    const resp = await restClient.post(cartUrl(`:cartApi/:cartId/assignEmail`), {email});

    return resp.data;
}

export async function updateCartQty(request: UpdateCartRequest, itemId: number): Promise<CartItem> {
    const resp = await restClient.put(cartUrl(`:cartApi/:cartId/items/${itemId}`), request);

    return resp.data as CartItem;
}

export async function deleteCartItem(itemId: number): Promise<boolean> {
    const resp = await restClient.delete(cartUrl(`:cartApi/:cartId/items/${itemId}`));

    return resp.data as boolean;
}

export async function getTotals(): Promise<Totals> {
    const resp = await restClient.get(cartUrl(':cartApi/:cartId/totals'));

    await giftcard.changeGiftCardAppliedBasedOnTotals(resp.data);

    return resp.data as Totals;
}

export async function estimateShipping(request: EstimateShippingRequest): Promise<ShippingMethod[]> {
    // workaround per bug magento, se passiamo gli extension attributes questa chiamata è buggata, vedi
    // htdocs/vendor/magento/module-quote/Model/ShippingMethodManagement.php:300
    // $shippingAddress->addData($this->extractAddressData($address));
    if (request.address.hasOwnProperty('extension_attributes')) {
        delete request.address.extension_attributes;
    }

    if (request.address.hasOwnProperty('custom_attributes')) {
        delete request.address.custom_attributes;
    }

    if (request.address.hasOwnProperty('error')) {
        delete request.address.error;
    }

    const resp = await restClient.post(cartUrl(':cartApi/:cartId/estimate-shipping-methods'), request);

    return resp.data as ShippingMethod[];
}

export async function getPaymentMethods(billingCountryCode: string = ''): Promise<PaymentMethod[]> {
    const resp = await restClient.post(cartUrl(':cartApi/:cartId/payment-methods-billing'), {billingCountryCode});

    return resp.data as PaymentMethod[];
}

export async function getCoupon(): Promise<any> {
    const resp = await restClient.get(cartUrl(':cartApi/:cartId/coupons'));

    return resp.data;
}

export async function doApplyCoupon(couponCode: string): Promise<boolean> {
    const resp = await restClient.put(cartUrl(`:cartApi/:cartId/coupons/${couponCode}`));

    return resp.data as boolean;
}

export async function deleteCoupon(): Promise<boolean> {
    const resp = await restClient.delete(cartUrl(':cartApi/:cartId/coupons/'));

    return resp.data as boolean;
}

export async function assignAddress(request: AssignAddressRequest): Promise<string> {
    const resp = await restClient.post(cartUrl(':cartApi/:cartId/billing-address'), request);

    return resp.data as string;
}

export async function updateShippingAddress(address: QuoteAddress): Promise<string> {
    const resp = await restClient.post(cartUrl(':cartApi/:cartId/updateShippingAddress'), {address});

    return resp.data as string;
}

export async function updateRequestInvoice(requestInvoice: boolean | null, params = {}): Promise<string> {
    const resp = await restClient.post(cartUrl(':cartApi/:cartId/requestInvoice'), {requestInvoice}, {params});

    return resp.data as string;
}

export async function updateShippingTelephone(address: QuoteAddress): Promise<string> {
    const resp = await restClient.post(cartUrl(':cartApi/:cartId/updateShippingTelephone'), {address});

    return resp.data as string;
}

export async function getSelectedPaymentMethod(): Promise<string> {
    const resp = await restClient.get(cartUrl(':cartApi/:cartId/selected-payment-method'));

    return resp.data.method as string;
}

export async function setPaymentMethod(request: UpdatePaymentRequest): Promise<string> {
    const resp = await restClient.put(cartUrl(':cartApi/:cartId/selected-payment-method'), request);

    return resp.data as string;
}

export async function getShippingMethods(): Promise<ShippingMethod[]> {
    const resp = await restClient.get(cartUrl(':cartApi/:cartId/shipping-methods'));

    return resp.data as ShippingMethod[];
}

export async function setShippingInformation(request: ShipInfoReq): Promise<ShipInfoResp> {
    const resp = await restClient.post(cartUrl(':cartApi/:cartId/shipping-information'), request);

    return resp.data as ShipInfoResp;
}

export async function setPaymentInformation(request: PaymentInformationRequest): Promise<number> {
    const resp = await restClient.post(cartUrl(':cartApi/:cartId/payment-information'), request);

    return parseInt(resp.data, 10) as number;
}

export async function createMagentoOrder(request: PaymentInformationRequest): Promise<any> {
    const resp = await restClient.post(cartUrl(':cartApi/:cartId/place-order'), request);

    return resp.data;
}

export async function updateGift(request: UpdateGiftRequest): Promise<Gift> {
    const resp = await restClient.put(cartUrl(`:cartApi/:cartId/updateGift`), request);

    return resp.data as Gift;
}

export async function removeGift(): Promise<Gift> {
    const resp = await restClient.delete(cartUrl(`:cartApi/:cartId/removeGift`));

    return resp.data as Gift;
}

export async function resetReservedOrderId(): Promise<string> {
    const resp = await restClient.get(cartUrl(`:cartApi/:cartId/reset-reserved-order-id`));

    return resp.data;
}

export async function getHipayPayment(
    type: string,
    shouldTokenizeCard?: boolean,
    tokenizedCardId?: number,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    hipayToken?: string
): Promise<string> {
    const getParams: any = {};
    if (navigator) {
        getParams.java_enabled = navigator.javaEnabled();
        getParams.language = navigator.language;
    }
    if (window && window.screen) {
        getParams.color_depth = window.screen.colorDepth;
        getParams.screen_height = window.screen.height;
        getParams.screen_width = window.screen.width;
        getParams.timezone = new Date().getTimezoneOffset();
    }
    getParams.tokenize_card = shouldTokenizeCard ? 1 : 0;

    if (tokenizedCardId) {
        getParams.tokenized_card_id = tokenizedCardId;
    }

    const resp = await restClient.get(cartUrl(`:cartApi/:cartId/createHiPayPayment/${type}`), {
        params: getParams,
    });
    return resp.data;
}

export async function getGiftCardCredit(code: string): Promise<any> {
    const prefix = store.getters['user/isLoggedin'] === true ? '' : 'carts/';

    const resp = await restClient.get(cartUrl(`${prefix}:cartApi/:cartId/checkGiftCard/${code}`));

    return resp.data;
}

export async function applyGiftCard(code: string, amount: any): Promise<any> {
    const prefix = store.getters['user/isLoggedin'] === true ? '' : 'carts/';
    const req = cartUrl(`${prefix}:cartApi/:cartId/giftCards`);
    const payload = {
        giftCardAccountData: {
            giftCards: [code],
            giftCardsAmount: amount,
        },
    };

    const resp = await restClient.post(req, payload);

    return resp.data;
}

export async function removeGiftCard(code: string): Promise<any> {
    const prefix = store.getters['user/isLoggedin'] === true ? '' : 'carts/';
    const resp = await restClient.delete(cartUrl(`${prefix}:cartApi/:cartId/giftCards/${code}`));

    return resp.data;
}

export async function isSessionValid(): Promise<boolean> {
    const resp = await restClient.get(cartUrl(`:cartApi/:cartId/isSessionValid`));

    return resp.data;
}

export async function validateBeforePlace(billingAddress: QuoteAddress, email: string): Promise<boolean> {
    const resp = await restClient.post(cartUrl(`:cartApi/:cartId/checkAddressBeforePay`), {billingAddress, email});

    return resp.data;
}

export async function setShippingOption(shippingOptionCode: string, shippingOptionValue: boolean): Promise<any> {
    const resp = await restClient.post(cartUrl(':cartApi/:cartId/setShippingOption'), {
        shippingOptionCode,
        shippingOptionValue,
    });

    return resp.data;
}

export async function removeShippingOptions(): Promise<any> {
    const resp = await restClient.put(cartUrl(':cartApi/:cartId/removeShippingOptions'));

    return resp.data;
}

export async function oneyRates(): Promise<string> {
    const resp = await restClient.post(cartUrl(':cartApi/:cartId/estimateOneyRates'));

    return resp.data;
}

export async function socialLogin(social: string): Promise<string> {
    const resp = await restClient.post(`/socialsignin/login/${social}`);
    return resp.data;
}

export async function addWishlist(sku: string): Promise<any> {
    const resp = await restClient.post('wishlist/add', {sku});
    return resp.data;
}

export async function executeIntent(paypalOrderId: string): Promise<any> {
    const resp = await restClient.post(`paypal-orders/executeIntent`, {paypalOrderId});
    return resp.data;
}

export async function updateQuoteBenefits(benefits: LoyaltyBenefit[]): Promise<any> {
    const resp = await restClient.post(cartUrl(':cartApi/:cartId/updateBenefits'), {
        benefits,
    });

    return resp.data;
}

export async function reloadCustomerBenefits(): Promise<any> {
    const resp = await restClient.post(cartUrl(':cartApi/:cartId/reloadCustomerBenefits'));

    return resp.data;
}

export async function resetQuoteBenefits(): Promise<any> {
    const resp = await restClient.post(cartUrl(':cartApi/:cartId/resetBenefits'), {});

    return resp.data;
}

export async function consumeMessages(): Promise<Message> {
    const resp = await restClient.post('consumeMessages');

    return resp.data;
}

export async function redirectToScalapay(paymentMethod: string): Promise<any> {
    const resp = await axios.get(
        `${store.state.options.base_url}scalapay/redirect/index?ajax=true&inPage=true&paymentMethod=${paymentMethod}`
    );

    return resp.data;
}

export async function deletePaypalCustomerVault(id: string): Promise<number> {
    const resp = await restClient.delete(`paypal-orders/customerVault/${id}`);

    return resp.status;
}

export async function deleteTokenizedCard(tokenizedCardId: number): Promise<number> {
    const resp = await restClient.delete(`hipay/tokenizedCard/${tokenizedCardId}`);

    return resp.status;
}

export async function klarnaWaitAuthCallback(authToken: string): Promise<any> {
    const controllerClient = buildControllerRestClient();
    const resp = await controllerClient.post('checkout/klarna/quoteStatus', {authorization_token: authToken});
    return resp.data;
}

export async function klarnaUpdateSession(authToken: string): Promise<any> {
    const controllerClient = buildControllerRestClient();
    const resp = await controllerClient.post('checkout/klarna/updateSession', {authorization_token: authToken});
    return resp.data;
}

export async function getCustomerSections(sections: string): Promise<any> {
    const controllerClient = buildControllerRestClient();
    const resp = await controllerClient.get(`customer/section/load?${new URLSearchParams({sections}).toString()}`);
    return resp.data;
}

export async function getCartContext(): Promise<any> {
    const resp = await restClient.get(cartUrl(`:cartApi/:cartId/cart-context`));

    return JSON.parse(resp.data);
}

export async function stripeGetPaymentMethods(billingAddress: QuoteAddress): Promise<any> {
    const resp = await restClient.post('stripe/payments/get_checkout_payment_methods', {billingAddress});

    return JSON.parse(resp.data);
}